import {ActionMenu} from '@primer/react-brand/esm'
import {useMemo, type ComponentProps, type Key} from 'react'

import {useDetectedMobileOS} from '../../../lib/hooks/use-detected-os'
import type {DownloadOptions} from '../../../schemas/contentful/contentTypes/downloadOptions'
import {
  renderOSAwareDownloadActionMenu,
  useOSAwareDownloadActionMenu,
  type OSAwareDownloadActionMenuAnalytics,
  type OSAwareDownloadActionMenuController,
  type OSAwareDownloadActionMenuElement,
} from '../../OSAwareDownloadActionMenu'
import {
  getNativeShareCopy,
  renderNativeShareButton,
  renderNativeShareFeedback,
  useNativeShareAction,
  type NativeShareActionController,
  type NativeShareButtonElement,
} from '../../NativeShareAction/NativeShareAction'
import {
  toOSAwareDownloadActions,
  toResolvedDownloadOptions,
  type ResolvedDownloadOptions,
} from './resolve-download-options'

export type ContentfulDownloadActionMenuProps = {
  component: DownloadOptions
  analytics?: OSAwareDownloadActionMenuAnalytics
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
}

export type RenderContentfulDownloadActionMenuOptions = {
  component?: DownloadOptions
  resolved?: ResolvedContentfulDownloadOptions
  analytics?: OSAwareDownloadActionMenuAnalytics
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
  elementKey?: Key
}

export type ContentfulDownloadActionMenuElement = OSAwareDownloadActionMenuElement
export type ContentfulDownloadActionElement = OSAwareDownloadActionMenuElement | NativeShareButtonElement
export type ResolvedContentfulDownloadOptions = ResolvedDownloadOptions & {
  actionMenuId: string
  controller: OSAwareDownloadActionMenuController
  nativeShareController: NativeShareActionController
  shouldUseNativeShare: boolean
}

/**
 * Resolves OS/architecture-aware download options for `component`, always
 * calling hooks unconditionally so callers can use it even when `component`
 * isn't available yet (e.g. an unresolved Contentful reference). Architecture
 * detection is skipped whenever `component` is absent.
 */
export function useResolvedDownloadOptions(component?: DownloadOptions): ResolvedContentfulDownloadOptions | undefined {
  const actions = useMemo(() => (component ? toOSAwareDownloadActions(component) : undefined), [component])
  const controller = useOSAwareDownloadActionMenu(actions, component?.fields.menuLabel)
  const detectedMobileOS = useDetectedMobileOS()
  const nativeShareCtaLabel = component?.fields.nativeMobileShareCtaLabel?.trim()
  const nativeShareCopy = useMemo(() => getNativeShareCopy(nativeShareCtaLabel || undefined), [nativeShareCtaLabel])
  const nativeShareController = useNativeShareAction({copy: nativeShareCopy})

  return useMemo(() => {
    if (!component || !controller) return undefined

    return {
      ...toResolvedDownloadOptions(component, controller),
      actionMenuId: controller.actionMenuId,
      controller,
      nativeShareController,
      shouldUseNativeShare: component.fields.enableNativeMobileShare === true && detectedMobileOS !== undefined,
    }
  }, [component, controller, detectedMobileOS, nativeShareController])
}

/**
 * Returns either Primer Brand's direct `Button` for eligible native-share
 * visitors or the direct `ActionMenu` download experience for everyone else.
 */
export function renderContentfulDownloadAction(
  options: RenderContentfulDownloadActionMenuOptions & {
    component: DownloadOptions
    resolved: ResolvedContentfulDownloadOptions
  },
): ContentfulDownloadActionElement
export function renderContentfulDownloadAction(
  options: RenderContentfulDownloadActionMenuOptions,
): ContentfulDownloadActionElement | null
export function renderContentfulDownloadAction({
  component,
  resolved,
  analytics,
  size,
  variant,
  elementKey,
}: RenderContentfulDownloadActionMenuOptions): ContentfulDownloadActionElement | null {
  if (!component || !resolved) return null

  if (resolved.shouldUseNativeShare) {
    return renderNativeShareButton({
      analytics,
      controller: resolved.nativeShareController,
      elementKey,
      size,
      variant,
    })
  }

  return renderContentfulDownloadActionMenu({
    component,
    resolved,
    analytics,
    size,
    variant,
    elementKey,
  })
}

export function renderContentfulDownloadActionFeedback(
  resolved: ResolvedContentfulDownloadOptions | undefined,
  className?: string,
) {
  if (!resolved?.shouldUseNativeShare) return null

  return renderNativeShareFeedback(resolved.nativeShareController, className)
}

/**
 * Pure render helper whose returned element's `type` is directly Primer
 * Brand's `ActionMenu` (not a wrapper). Primer Brand's `Hero.ButtonGroup` /
 * `CTABanner.ButtonGroup` filter children by exact element type and only keep
 * direct `Button`/`ActionMenu` elements, so this lets Flex Suite parents
 * render a compliant direct child instead of a `ContentfulDownloadActionMenu`
 * wrapper. Returns `null` when `component` or `resolved` is missing.
 */
export function renderContentfulDownloadActionMenu(
  options: RenderContentfulDownloadActionMenuOptions & {
    component: DownloadOptions
    resolved: ResolvedContentfulDownloadOptions
  },
): ContentfulDownloadActionMenuElement
export function renderContentfulDownloadActionMenu(
  options: RenderContentfulDownloadActionMenuOptions,
): ContentfulDownloadActionMenuElement | null
export function renderContentfulDownloadActionMenu({
  component,
  resolved,
  analytics,
  size,
  variant,
  elementKey,
}: RenderContentfulDownloadActionMenuOptions): ContentfulDownloadActionMenuElement | null {
  if (!component || !resolved) return null

  return renderOSAwareDownloadActionMenu({
    controller: resolved.controller,
    menuLabel: component.fields.menuLabel,
    analytics,
    size,
    variant,
    elementKey,
  })
}

/**
 * Standalone wrapper for stories/tests. Flex Suite parents that need a direct
 * `Button` or `ActionMenu` child should instead compose
 * `useResolvedDownloadOptions` + `renderContentfulDownloadAction`, then render
 * `renderContentfulDownloadActionFeedback` outside the ButtonGroup.
 */
export function ContentfulDownloadActionMenu({component, analytics, size, variant}: ContentfulDownloadActionMenuProps) {
  const resolved = useResolvedDownloadOptions(component)

  return (
    <>
      {renderContentfulDownloadAction({
        component,
        resolved,
        analytics,
        size,
        variant,
      })}
      {renderContentfulDownloadActionFeedback(resolved)}
    </>
  )
}
